import * as THREE from 'three';

export interface CameraSettings {
  distance: number;
  heightOffset: number;
  fov: number;
  sensitivityX: number;
  sensitivityY: number;
  invertX: boolean;
  invertY: boolean;
  offsetX: number;
  offsetY: number;
  minPitchDeg: number;
  maxPitchDeg: number;
  positionDamping: number;
  collisionPadding: number;
}

export const DEFAULT_CAMERA_SETTINGS: CameraSettings = {
  distance: 4.5,
  heightOffset: 2.0,
  fov: 60,
  sensitivityX: 0.15,
  sensitivityY: 0.12,
  invertX: false,
  invertY: false,
  offsetX: 0,
  offsetY: 0,
  minPitchDeg: -60,
  maxPitchDeg: 40,
  positionDamping: 0.12,
  collisionPadding: 0.3,
};

const WORLD_UP = new THREE.Vector3(0, 1, 0);
/** Ketinggian titik pandang default di atas kaki karakter: tengah badan
 *  slime (~1.1 tinggi, jari-jari 0.55). Kamera default membidik ke sini
 *  sehingga titik putih AIM jatuh tepat di tengah slime (bukan di atas
 *  kepalanya). */
const LOOK_AT_BODY_HEIGHT = 0.6;
/** Radius bola sapu tabrakan kamera: boom + gerak kamera dianggap bola
 *  (bukan ray tipis) supaya tidak selip lewat tepi/sudut dinding. */
const SWEEP_RADIUS = 0.25;
/** Jarak boom minimum: kamera tak pernah lebih dekat dari ini ke pivot. */
const MIN_BOOM = 0.5;

export interface CameraTarget {
  position: THREE.Vector3;
}

export class ThirdPersonCamera {
  readonly camera: THREE.PerspectiveCamera;
  private settings: CameraSettings;  private yaw = 0;
  private pitch = 0;
  private target: CameraTarget;
  private colliders: THREE.Object3D[];
  /** Objek dinamis (crate) yang ikut menahan kamera. Array dipegang
   *  by-ref (ref pushables stabil lintas loadStage); mesh dibaca per frame. */
  private dynamics: Array<{ mesh: THREE.Object3D }> = [];
  private raycaster = new THREE.Raycaster();
  private desiredPos = new THREE.Vector3();
  private pivot = new THREE.Vector3();
  private lookAtPoint = new THREE.Vector3();
  private tmpRight = new THREE.Vector3();
  private tmpUp = new THREE.Vector3();
  private tmpOffset = new THREE.Vector3();
  private tmpDir = new THREE.Vector3();
  private tmpOrigin = new THREE.Vector3();
  private tmpBoomOrigin = new THREE.Vector3();
  private tmpMove = new THREE.Vector3();
  private tmpMoveRight = new THREE.Vector3();
  private tmpMoveUp = new THREE.Vector3();
  private tmpAxial = new THREE.Vector3();
  private tmpLateral = new THREE.Vector3();
  private steadyAim = false;
  private trackPoint: THREE.Vector3 | null = null;
  /** Jarak boom efektif (dihaluskan): targetnya bisa snap (halangan),
   *  tapi nilai ini mengejar cepat saat menciut + lambat saat memanjang
   *  supaya kamera tak teleport (pop) saat jatuh melewati platform. */
  private boomDist: number;
  /** Target boom yang SUDAH dihaluskan: ray tabrakan bisa flip-flop
   *  hit/miss tiap frame (jatuh melewati tepi/anak tangga) — tanpa ini
   *  boom ikut mengayun maju-mundur (surging). */
  private boomTarget: number;
  /** Histeresis tuck: butuh hit BERUNTUN untuk menekuk, miss BERUNTUN
   *  untuk melepas. Ray yang genit (selang-seling) tak pernah tuck. */
  private tucked = false;
  private tuckedDist: number;
  private blockedFrames = 0;
  private clearFrames = 0;

  constructor(
    camera: THREE.PerspectiveCamera,
    target: CameraTarget,
    colliders: THREE.Object3D[] = [],
    settings: CameraSettings = { ...DEFAULT_CAMERA_SETTINGS },
  ) {
    this.camera = camera;
    this.target = target;
    this.colliders = colliders;
    this.settings = settings;
    this.boomDist = settings.distance;
    this.boomTarget = settings.distance;
    this.tuckedDist = settings.distance;
    this.camera.fov = settings.fov;
    this.camera.updateProjectionMatrix();
  }

  get currentYaw(): number {
    return this.yaw;
  }

  get baseDistance(): number {
    return this.settings.distance;
  }

  /** Saat true, kamera menempel kaku (tanpa lerp) — untuk aim presisi. */
  setSteadyAim(steady: boolean): void {
    this.steadyAim = steady;
  }

  /**
   * Kunci bidik ke titik dunia (mis. anchor grapple): titik itu terkunci
   * di tengah layar walau character bergerak. null = lepas dengan
   * handoff mulus (yaw/pitch diambil dari arah pandang saat ini).
   */
  setTrackPoint(p: THREE.Vector3 | null): void {
    if (p) {
      if (!this.trackPoint) this.trackPoint = new THREE.Vector3();
      this.trackPoint.copy(p);
      return;
    }
    if (this.trackPoint) {
      // Handoff mulus ke framing default (tinggi badan): yaw/pitch rig
      // diarahkan ke titik badan dari posisi kamera saat ini — view
      // melanjutkan dari posisi terkunci, bukan lompat ke pivot-atas.
      this.camera.updateMatrixWorld();
      const t = this.target.position;
      this.tmpDir
        .set(t.x, t.y + LOOK_AT_BODY_HEIGHT, t.z)
        .sub(this.camera.position)
        .normalize();
      this.yaw = Math.atan2(this.tmpDir.x, this.tmpDir.z);
      this.pitch = THREE.MathUtils.clamp(
        Math.asin(THREE.MathUtils.clamp(this.tmpDir.y, -1, 1)),
        THREE.MathUtils.degToRad(this.settings.minPitchDeg),
        THREE.MathUtils.degToRad(this.settings.maxPitchDeg),
      );
    }
    this.trackPoint = null;
  }

  applySettings(patch: Partial<CameraSettings>): void {
    Object.assign(this.settings, patch);

    this.pitch = THREE.MathUtils.clamp(
      this.pitch,
      THREE.MathUtils.degToRad(this.settings.minPitchDeg),
      THREE.MathUtils.degToRad(this.settings.maxPitchDeg),
    );

    if (patch.fov !== undefined) {
      this.camera.fov = this.settings.fov;
      this.camera.updateProjectionMatrix();
    }
  }

  applyMouseDelta(deltaX: number, deltaY: number): void {
    const signX = this.settings.invertX ? 1 : -1;
    const signY = this.settings.invertY ? 1 : -1;

    this.yaw += signX * deltaX * this.settings.sensitivityX * 0.01;
    this.pitch += signY * deltaY * this.settings.sensitivityY * 0.01;
    this.pitch = THREE.MathUtils.clamp(
      this.pitch,
      THREE.MathUtils.degToRad(this.settings.minPitchDeg),
      THREE.MathUtils.degToRad(this.settings.maxPitchDeg),
    );
  }

  update(delta: number): void {
    const s = this.settings;
    const t = this.target.position;
    this.pivot.set(t.x, t.y + s.heightOffset, t.z);

    const dir = new THREE.Vector3(
      Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      Math.cos(this.yaw) * Math.cos(this.pitch),
    );

    this.tmpRight.crossVectors(WORLD_UP, dir).normalize();
    this.tmpUp.crossVectors(this.tmpRight, dir).multiplyScalar(-1).normalize();
    this.tmpOffset
      .set(0, 0, 0)
      .addScaledVector(this.tmpRight, s.offsetX)
      .addScaledVector(this.tmpUp, s.offsetY);

    this.desiredPos.copy(this.pivot).addScaledVector(dir, s.distance).add(this.tmpOffset);
    // Bidik ke tengah badan slime (bukan ke pivot at heightOffset di atas
    // kepala): dengan pitch default 0, slime tampil tepat di tengah layar
    // tepat di bawah titik putih AIM. offsetY user tetap ikut.
    this.lookAtPoint.set(t.x, t.y + LOOK_AT_BODY_HEIGHT, t.z).add(this.tmpOffset);

    // Tabrakan boom tiga lapis + sapuan bola: (0) query sapuan
    // (bola, bukan ray tipis) mencakup dinding + objek dinamis (crate)
    // sehingga tak selip lewat tepi/sudut; (1) histeresis tuck — hit
    // beruntun menekuk, miss beruntun melepas, ray genit selang-seling
    // diabaikan; (2) target dihaluskan (masuk cepat anti-klip, keluar
    // lambat anti-pop); (3) boom mengejar target (menciut cepat,
    // memanjang lambat). Jatuh dari ketinggian melewati platform:
    // tak teleport, tak mengayun maju-mundur.
    let rawTarget = s.distance;
    if (this.colliders.length > 0 || this.dynamics.length > 0) {
      // Sapu dari PUSAT PANDANG (pivot + offset) supaya segmen yang
      // terverifikasi berakhir tepat di desiredPos.
      this.tmpBoomOrigin.copy(this.pivot).add(this.tmpOffset);
      const hit = this.sweepDistance(this.tmpBoomOrigin, dir, s.distance, this.tmpRight, this.tmpUp);
      if (hit < s.distance) {
        this.tuckedDist = Math.max(MIN_BOOM, hit - s.collisionPadding);
        this.blockedFrames++;
        this.clearFrames = 0;
      } else {
        this.blockedFrames = 0;
        this.clearFrames++;
      }
    } else {
      this.blockedFrames = 0;
      this.clearFrames++;
    }
    if (!this.tucked && this.blockedFrames >= 2) this.tucked = true;
    if (this.tucked && this.clearFrames >= 4) this.tucked = false;
    if (this.tucked) rawTarget = Math.min(this.tuckedDist, s.distance);
    const targetRate = rawTarget < this.boomTarget ? 16 : 5;
    this.boomTarget += (rawTarget - this.boomTarget) * (1 - Math.exp(-targetRate * delta));
    const boomRate = this.boomTarget < this.boomDist ? 14 : 3;
    this.boomDist += (this.boomTarget - this.boomDist) * (1 - Math.exp(-boomRate * delta));
    this.desiredPos.copy(this.pivot).addScaledVector(dir, this.boomDist).add(this.tmpOffset);

    const alpha = 1 - Math.pow(s.positionDamping, delta * 60);
    // Titik langkah: steadyAim = langsung target, normal = lerp halus.
    // Keduanya dilewatkan sapuan segmen supaya kamera tak pernah
    // menembus dinding/objek saat target melompat (respawn, ganti stage).
    if (this.steadyAim) {
      this.tmpMove.copy(this.desiredPos);
    } else {
      this.tmpMove.copy(this.camera.position).lerp(this.desiredPos, THREE.MathUtils.clamp(alpha, 0, 1));
    }
    this.moveCameraNoTunnel(this.tmpMove, dir, s.collisionPadding);
    this.camera.lookAt(this.trackPoint ?? this.lookAtPoint);
  }

  /**
   * Pindahkan kamera ke `goal` tanpa menembus geometri. Gerak dipecah:
   * - aksial (sepanjang boom dir): bebas — pemiliknya logika tuck/extend
   *   boom yang sudah menyapu segmen pivot -> desired (pull-in melewati
   *   bidang dinding menuju sisi dekat adalah perilaku yang benar);
   * - lateral (tegak lurus boom): disapu bola dan dijepit sebelum
   *   halangan, sehingga ayunan/yaw tak memotong sudut dinding.
   */
  private moveCameraNoTunnel(goal: THREE.Vector3, boomDir: THREE.Vector3, padding: number): void {
    this.tmpDir.subVectors(goal, this.camera.position);
    const dist = this.tmpDir.length();
    if (dist < 1e-6) return;
    this.tmpDir.multiplyScalar(1 / dist);
    // Komponen aksial bebas, komponen lateral disapu.
    const axialLen = this.tmpDir.dot(boomDir);
    this.tmpAxial.copy(boomDir).multiplyScalar(axialLen * dist);
    this.tmpLateral.copy(this.tmpDir).multiplyScalar(dist).sub(this.tmpAxial);
    const latLen = this.tmpLateral.length();
    let latTravel = latLen;
    if (latLen > 1e-6) {
      this.tmpLateral.multiplyScalar(1 / latLen);
      const right = this.tmpMoveRight.crossVectors(WORLD_UP, this.tmpLateral);
      if (right.lengthSq() < 1e-8) right.set(1, 0, 0);
      else right.normalize();
      const up = this.tmpMoveUp.crossVectors(this.tmpLateral, right).normalize();
      const free = this.sweepDistance(this.camera.position, this.tmpLateral, latLen, right, up);
      latTravel = free < latLen ? Math.max(0, free - padding) : latLen;
    }
    this.camera.position.add(this.tmpAxial).addScaledVector(this.tmpLateral, latTravel);
  }

  setColliders(colliders: THREE.Object3D[]): void {
    this.colliders = colliders;
  }

  /** Daftarkan objek dinamis (mis. crate) supaya kamera tak tembus objek. */
  setDynamics(items: Array<{ mesh: THREE.Object3D }>): void {
    this.dynamics = items;
  }

  /** Kumpulkan semua mesh tabrakan statis + dinamis untuk query sapuan. */
  private collectSweepTargets(out: THREE.Object3D[]): THREE.Object3D[] {
    out.length = 0;
    for (const c of this.colliders) out.push(c);
    for (const d of this.dynamics) {
      if (d.mesh.visible) out.push(d.mesh);
    }
    return out;
  }

  private sweepTargets: THREE.Object3D[] = [];

  /**
   * Jarak bebas tabrakan dari `origin` sepanjang `dir` (sapuan bola:
   * ray tengah + 4 ray offset tegak lurus, diambil yang paling dekat).
   * Mengembalikan maxDist bila tidak ada halangan.
   */
  private sweepDistance(
    origin: THREE.Vector3,
    dir: THREE.Vector3,
    maxDist: number,
    right: THREE.Vector3,
    up: THREE.Vector3,
  ): number {
    const targets = this.collectSweepTargets(this.sweepTargets);
    if (targets.length === 0) return maxDist;
    let best = maxDist;
    // 5 ray: tengah + atas/bawah/kiri/kanan sejauh SWEEP_RADIUS.
    for (let i = 0; i < 5; i++) {
      const ox = i === 1 ? SWEEP_RADIUS : i === 2 ? -SWEEP_RADIUS : 0;
      const oy = i === 3 ? SWEEP_RADIUS : i === 4 ? -SWEEP_RADIUS : 0;
      this.tmpOrigin.copy(origin).addScaledVector(right, ox).addScaledVector(up, oy);
      this.raycaster.set(this.tmpOrigin, dir);
      this.raycaster.far = maxDist;
      const hits = this.raycaster.intersectObjects(targets, true);
      if (hits.length > 0 && hits[0].distance < best) best = hits[0].distance;
    }
    return best;
  }
}